import { memo, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Input,
  Multiselect,
  MultiselectProps,
  SpaceBetween,
} from '@amzn/awsui-components-core';
import { TaskSkill } from '@amzn/awscat-pattern-library-service-model';

import { getSkillLabels, getTaskSkills, orderSkillOptions } from '../utils/TaskUtils';
import { TaskSkillOptions } from '../utils/SelectableOptions';

const enumValueSet = new Set(
  TaskSkillOptions.map((o) => o.value?.toUpperCase()).filter((v): v is string => v !== undefined),
);

function splitSkills(skills: string): string[] {
  return skills ? skills.split(',').filter(Boolean) : [];
}

function parseCustomValues(skills: string): string {
  return splitSkills(skills)
    .filter((v) => !enumValueSet.has(v.toUpperCase()))
    .join(', ');
}

interface PatternCellSkillEditorInputsProps {
  readonly skills: string;
  readonly onSkillsChange?: (skills: string) => void;
}

function PatternCellSkillEditorInputs({
  skills,
  onSkillsChange,
}: PatternCellSkillEditorInputsProps) {
  const orderedOptions = useMemo(() => orderSkillOptions(TaskSkillOptions), []);

  // Custom input keeps raw user text (trailing commas, spaces) that the round-trip
  // through getTaskSkills would otherwise strip — so we hold it as local state,
  // initialized once from skills on mount.
  const [customInput, setCustomInput] = useState(() => parseCustomValues(skills));

  // When skills changes, recompute which enum options are selected.
  const selectedOptions = useMemo(() => {
    const selectValues = splitSkills(skills).filter((v) => enumValueSet.has(v.toUpperCase()));
    return orderedOptions.filter((o) => o.value && selectValues.includes(o.value));
  }, [skills, orderedOptions]);

  // When the user changes the multiselect, merge with custom text and notify parent.
  const handleMultiselectChange = useCallback(
    (event: { detail: MultiselectProps.MultiselectChangeDetail }) => {
      const selectValues = event.detail.selectedOptions
        .map((o) => o.value)
        .filter((v): v is string => v !== undefined);
      onSkillsChange?.(getTaskSkills(selectValues, customInput));
    },
    [customInput, onSkillsChange],
  );

  // When the user types in the custom input, merge with selection and notify parent.
  const handleCustomInputChange = useCallback(
    (event: { detail: { value: string } }) => {
      const value = event.detail.value;
      setCustomInput(value);
      const selectValues = selectedOptions
        .map((o) => o.value)
        .filter((v): v is string => v !== undefined);
      onSkillsChange?.(getTaskSkills(selectValues, value));
    },
    [selectedOptions, onSkillsChange],
  );

  // Show the custom input when "Other" is selected or custom text is non-empty.
  const isCustomInputVisible =
    selectedOptions.some((o) => o.value === TaskSkill.Other) || customInput !== '';

  return (
    <SpaceBetween size="xs">
      <Multiselect
        selectedOptions={selectedOptions}
        onChange={handleMultiselectChange}
        options={orderedOptions}
        placeholder="Select skills"
        filteringType="auto"
        keepOpen={false}
      />
      {isCustomInputVisible && (
        <Input
          value={customInput}
          onChange={handleCustomInputChange}
          placeholder="Custom skills (comma-separated)"
        />
      )}
    </SpaceBetween>
  );
}

interface PatternCellSkillEditorProps {
  readonly skills: string;
  readonly isReadonly: boolean;
  readonly onSkillsChange?: (skills: string) => void;
}

/**
 * Renders the "Skills required" cell in the tasks table. In readonly
 * mode shows comma-joined display labels. In editable mode renders a
 * CloudScape Multiselect for the TaskSkill enum values plus a text
 * Input for custom freeform skills (matching legacy
 * MultiselectCustomInput behavior).
 */
export const PatternCellSkillEditor = memo(function PatternCellSkillEditor({
  skills,
  isReadonly,
  onSkillsChange,
}: PatternCellSkillEditorProps) {
  if (isReadonly) {
    return (
      <Box variant="span" color="text-body-secondary">
        {skills ? getSkillLabels(skills) : '—'}
      </Box>
    );
  }

  return <PatternCellSkillEditorInputs skills={skills} onSkillsChange={onSkillsChange} />;
});
